// AI-CONTEXT: See docs/features/dashboard-participation-ladder-growth-card.md
import { useMemo, useCallback } from 'react'
import useDashboardCardSetting from './useDashboardCardSetting'
import { DASHBOARD_FILTER_FIELDS } from '../helpers/dashboardHelpers/dashboardCardFilters'
import { recordInLocation } from '../helpers/locations/locationFilter'

/**
 * Filter state for the participatieladder growth card: location, traject and a
 * single persona. Each selection initialises from the `DashboardItems` doc and
 * is persisted back so it survives a reload (mirrors `useLocationFilter`).
 *
 * @param {Array<object>} personas - all personas to filter.
 * @param {object} [options]
 * @param {object} [options.item] - the static-card `DashboardItems` doc (needs `docid`).
 * @returns {{
 *   filteredPersonas: Array<object>,
 *   selectedLocationId: string,
 *   setSelectedLocationId: (id: string) => void,
 *   selectedRouteId: string,
 *   setSelectedRouteId: (id: string) => void,
 *   selectedPersonaId: string,
 *   setSelectedPersonaId: (id: string) => void,
 *   activeFilterCount: number,
 *   clear: () => void,
 * }}
 */
const useParticipationLadderFilters = (personas, options = {}) => {
    const { item } = options

    // Drie keuzes, drie velden op hetzelfde DashboardItems-doc. Het
    // schrijfrecept stond hier eerder als eigen `persist`-functie, naast een
    // identieke kopie in useLocationFilter; nu delen ze er één.
    // AI-CONTEXT: See docs/pitfalls.md#dev-copied-recipe-instead-of-shared-hook
    const [selectedLocationId, setSelectedLocationId] = useDashboardCardSetting(
        item, DASHBOARD_FILTER_FIELDS.SelectedLocationId, '',
    )
    const [selectedRouteId, setSelectedRouteId] = useDashboardCardSetting(
        item, DASHBOARD_FILTER_FIELDS.SelectedRouteId, '',
    )
    const [selectedPersonaId, setSelectedPersonaId] = useDashboardCardSetting(
        item, DASHBOARD_FILTER_FIELDS.SelectedPersonaId, '',
    )

    const clear = useCallback(() => {
        setSelectedLocationId('')
        setSelectedRouteId('')
        setSelectedPersonaId('')
    }, [setSelectedLocationId, setSelectedRouteId, setSelectedPersonaId])

    const filteredPersonas = useMemo(() => {
        const list = Array.isArray(personas) ? personas : []
        return list.filter((p) => {
            if (!recordInLocation(p, selectedLocationId)) return false
            if (selectedRouteId && p?.RouteID !== selectedRouteId) return false
            if (selectedPersonaId && p?.ID !== selectedPersonaId) return false
            return true
        })
    }, [personas, selectedLocationId, selectedRouteId, selectedPersonaId])

    const activeFilterCount =
        (selectedLocationId ? 1 : 0) + (selectedRouteId ? 1 : 0) + (selectedPersonaId ? 1 : 0)

    return {
        filteredPersonas,
        selectedLocationId,
        setSelectedLocationId,
        selectedRouteId,
        setSelectedRouteId,
        selectedPersonaId,
        setSelectedPersonaId,
        activeFilterCount,
        clear,
    }
}

export default useParticipationLadderFilters
